import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../../ui/UIElements/Icon";
import { Button } from "../../ui/UIElements/Button";
import UserCard from "../../ui/Users/UserCard";
import Pagination from "../../ui/Data/Pagination";
import UsersLoading from "../../ui/Users/UsersLoading";
import UsersError from "../../ui/Users/UsersError";
import { StatsGrid } from "../../ui/Data/StatCard";
import { useUsersData } from "../../../hooks/useUsers";
import { useExportUsers } from "../../../hooks/useExportUsers";
import { useUrlStates, usePostHog } from "@finitless/shared/hooks";
import { toast } from "sonner";
import {
  ITEMS_PER_PAGE,
  MAX_VISIBLE_PAGES,
  USER_STATUS_FILTER_TABS,
} from "../../../constants/userConstants";
import { PageHeader } from "@finitless/shared/components";
import "./customer-pages.css";

const CustomersPage = () => {
  const { t } = useTranslation(["customers", "agentTranslations"]);
  const navigate = useNavigate();

  const [urlStates, updateUrlState] = useUrlStates({
    enabledFilter: "all",
    searchTerm: "",
    currentPage: "1",
  });

  const [searchInput, setSearchInput] = useState(urlStates.searchTerm || "");
  const [enabledFilter, setEnabledFilter] = useState(
    urlStates.enabledFilter || "all",
  );
  const [searchTerm, setSearchTerm] = useState(urlStates.searchTerm || "");
  const [currentPage, setCurrentPage] = useState(
    parseInt(urlStates.currentPage) || 1,
  );

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Get customer data only
  const { stats, users, pagination, isLoading, error, filterTabCounts } =
    useUsersData("customers", enabledFilter, searchTerm, currentPage);

  // Export users hook
  const exportUsersMutation = useExportUsers();
  const { trackFeatureAdoption } = usePostHog();

  const paginationData = pagination;

  // Calculate totalPages manually if not provided by API
  const calculatedTotalPages = paginationData?.total
    ? Math.ceil(paginationData.total / ITEMS_PER_PAGE)
    : 1;

  const totalPages = paginationData?.totalPages || calculatedTotalPages;

  const getFilterLabel = (filterKey) => {
    const labelByFilter = {
      enabled: t("filters.active"),
      disabled: t("filters.inactive"),
      all: t("filters.allCustomers"),
    };
    return labelByFilter[filterKey] || filterKey;
  };

  const handleEnabledFilterChange = (newEnabledFilter) => {
    setEnabledFilter(newEnabledFilter);
    setCurrentPage(1);
    updateUrlState("enabledFilter", newEnabledFilter);
    updateUrlState("currentPage", "1");
  };

  const handleSearchChange = (term) => {
    setSearchInput(term);
    setCurrentPage(1);
    updateUrlState("searchTerm", term);
    updateUrlState("currentPage", "1");
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
    updateUrlState("searchTerm", "");
    updateUrlState("currentPage", "1");
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    updateUrlState("currentPage", page.toString());
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleViewDetails = (user) => {
    navigate(`/users/${user.id}`, { state: { user } });
  };

  const handleExportUsers = async () => {
    try {
      await exportUsersMutation.mutateAsync({
        userType: "customers",
        enabledFilter,
        searchTerm,
      });

      trackFeatureAdoption.dataExported({
        export_type: "users",
        format: "csv",
        user_type: "customers",
        enabled_filter: enabledFilter,
      });

      toast.success(t("toasts.exportSuccessTitle"), {
        description: t("toasts.exportSuccessDescription"),
        duration: 5000,
      });
    } catch {
      toast.error(t("toasts.exportFailedTitle"), {
        description: t("toasts.exportFailedDescription"),
        duration: 5000,
      });
    }
  };

  const metricCards = [
    {
      id: "total-customers",
      title: t("stats.totalCustomers"),
      value: stats?.totalcustomers || stats?.totalCustomers || 0,
      icon: "Users",
      iconColor: "text-[var(--success)]",
      iconBg: "bg-[var(--success-bg)]",
      gradient: false,
    },
  ];

  return (
    <div
      className="ordering-customers-page space-y-4 sm:space-y-6"
      data-ordering-surface="customers-list"
    >
      {/* Header */}
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
        <PageHeader
          title={t("header.title")}
          subtitle={t("subtitle.customers")}
        />
        <div className="flex gap-3">
          <Button
            type="button"
            onClick={handleExportUsers}
            disabled={exportUsersMutation.isPending}
            aria-busy={exportUsersMutation.isPending || undefined}
            emphasis="solid"
            intent="neutral"
            size="touch"
            className="px-3 sm:px-4"
          >
            <Icon name="Download" className="h-4 w-4" />
            <span className="hidden sm:inline">
              {exportUsersMutation.isPending
                ? t("actions.exporting")
                : t("actions.exportCustomers")}
            </span>
            <span className="sm:hidden">
              {exportUsersMutation.isPending ? "..." : t("actions.export")}
            </span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="ordering-customers-page__stat">
        <StatsGrid
          stats={metricCards}
          columns={metricCards.length}
          className="mb-4 sm:mb-6"
        />
      </div>

      {/* Status Tabs */}
      <div className="border-border border-b">
        <nav
          aria-label={t("header.title")}
          className="flex gap-4 overflow-x-auto sm:gap-8"
        >
          {USER_STATUS_FILTER_TABS.map(({ key }) => {
            const count = filterTabCounts[key] || 0;

            return (
              <Button
                variant="ghost"
                type="button"
                key={key}
                onClick={() => handleEnabledFilterChange(key)}
                aria-pressed={enabledFilter === key}
                className={`focus-visible:ring-ring min-h-11 cursor-pointer border-b-2 px-1 py-3 text-[length:var(--font-size-product-ui)] font-medium whitespace-nowrap transition-colors focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none sm:py-4 ${
                  enabledFilter === key
                    ? `border-current ${
                        key === "enabled"
                          ? "text-[var(--success)]"
                          : key === "disabled"
                            ? "text-destructive"
                            : "text-foreground"
                      }`
                    : "text-muted-foreground hover:text-foreground border-transparent hover:border-current"
                }`}
              >
                {getFilterLabel(key)} ({count})
              </Button>
            );
          })}
        </nav>
      </div>

      {/* Search */}
      <div className="border-border bg-card mb-6 rounded-lg border p-4 shadow-[var(--od-shadow-1)]">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 flex-col gap-4 sm:flex-row">
            {/* Search */}
            <div className="min-w-0 flex-1">
              <div className="relative" role="search">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Icon
                    name="Search"
                    className="text-muted-foreground h-5 w-5"
                  />
                </div>
                <input
                  type="text"
                  placeholder={t("search.placeholder", {
                    entity: t("tabs.customers").toLowerCase(),
                  })}
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  aria-label={t("search.placeholder", {
                    entity: t("tabs.customers").toLowerCase(),
                  })}
                  className="border-input bg-background text-foreground placeholder:text-muted-foreground focus-visible:ring-ring h-11 w-full rounded-md border px-4 py-2 pr-12 pl-10 text-[length:var(--font-size-product-ui)] shadow-sm transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none"
                />
                {searchInput && (
                  <Button
                    variant="ghost"
                    onClick={handleClearSearch}
                    aria-label={t("actions.clearSearch", {
                      ns: "agentTranslations",
                    })}
                    className="focus-visible:ring-ring text-muted-foreground hover:bg-accent hover:text-accent-foreground absolute top-1/2 right-0 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md transition-colors focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none rtl:right-auto rtl:left-0"
                    type="button"
                  >
                    <svg
                      aria-hidden="true"
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="ordering-customers-page__state">
          <UsersLoading />
        </div>
      ) : error ? (
        <div className="ordering-customers-page__state">
          <UsersError error={error} />
        </div>
      ) : users.length > 0 ? (
        <>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {users.map((user) => (
              <div
                key={user.id}
                role="button"
                tabIndex={0}
                data-customer-enabled={String(Boolean(user.enabled))}
                className="ordering-customers-page__customer-card focus-visible:ring-ring block w-full cursor-pointer rounded-lg text-left focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                onClick={() => handleViewDetails(user)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleViewDetails(user);
                  }
                }}
              >
                <UserCard user={user} />
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="border-border bg-card sticky bottom-0 z-[var(--layer-sticky)] mt-8 rounded-lg border p-4 shadow-[var(--od-shadow-2)]">
            <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
              <div className="text-muted-foreground text-sm">
                {t("pagination.showing", {
                  from: (currentPage - 1) * ITEMS_PER_PAGE + 1,
                  to: Math.min(
                    currentPage * ITEMS_PER_PAGE,
                    paginationData?.total || 0,
                  ),
                  total: paginationData?.total || 0,
                })}
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                maxVisiblePages={MAX_VISIBLE_PAGES}
                disabled={isLoading}
                className="mt-4 sm:mt-0"
              />
            </div>
          </div>
        </>
      ) : (
        <div className="border-border bg-card rounded-lg border px-4 py-12 text-center">
          <Icon
            name="Users"
            className="text-muted-foreground mx-auto mb-4 h-12 w-12"
          />
          <h3 className="text-foreground mt-2 text-sm font-medium">
            {t("empty.title")}
          </h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {searchTerm
              ? t("empty.subtitleSearch")
              : t("empty.subtitleDefault", { entity: t("tabs.customers") })}
          </p>
        </div>
      )}
    </div>
  );
};

export default CustomersPage;
